/**
 * Vue Observation — le tour EN COURS.
 *
 * Jusqu'ici la pipeline était une boîte noire pour le cockpit : un event avant
 * (`cyrano_generating` / `agent_thinking`), un event après (`cyrano_scheduled`),
 * et ~10-15 s de silence entre les deux — les spans ne sont persistés qu'en
 * bloc à `finalize`.
 *
 * Le backend diffuse désormais un event `pipeline_step` à la fermeture de
 * chaque étape (Francis#954, flag `PIPELINE_LIVE_STEPS_ENABLED`), sur le canal
 * SSE `conv:events:{creator}` déjà consommé. Cet état accumule ces étapes le
 * temps du tour, puis se vide.
 *
 * 🪤 Il ne remplace RIEN : c'est de l'éphémère, jamais une source de vérité. Le
 * tour complet — prompts, coûts, décision — reste celui que `finalize` persiste
 * et que la liste affiche une fois le tour terminé.
 */
import { atom, useSetAtom } from "jotai";
import { useCallback } from "react";

export interface LiveStep {
  name: string;
  spanType: string | null;
  durationMs: number | null;
  model: string | null;
  errored: boolean;
  endedAt: string | null;
}

export interface LiveTurn {
  /** `pipeline_run_id` du tour, quand une étape l'a porté. */
  runId: string | null;
  /** Horloge locale du début — sert le chronomètre affiché. */
  startedAt: number;
  steps: LiveStep[];
  /** Le tour a rendu son brouillon : on garde l'affichage un instant, figé. */
  done: boolean;
}

/** Tours en cours, par conversation. Éphémère : rien n'est persisté. */
export const liveTurnsAtom = atom<Record<string, LiveTurn>>({});

/**
 * Un tour qui ne se termine jamais (worker tué, event perdu) ne doit pas rester
 * affiché indéfiniment. Au-delà, la carte s'efface d'elle-même.
 */
export const LIVE_TURN_MAX_AGE_MS = 3 * 60_000;

/**
 * Retire le tour en cours d'une conversation. Appelé quand il est terminé ET
 * que la liste a été rafraîchie : le tour complet y figure alors, avec ses
 * prompts et son coût — garder la carte ferait doublon.
 */
export function useClearLiveTurn(): (conversationId: string) => void {
  const set = useSetAtom(liveTurnsAtom);
  return useCallback(
    (conversationId) =>
      set((prev) => {
        if (!(conversationId in prev)) return prev;
        const next = { ...prev };
        delete next[conversationId];
        return next;
      }),
    [set],
  );
}

/** Retire les tours trop vieux — appelé à chaque écriture, pas par un minuteur. */
export function pruneLiveTurns(prev: Record<string, LiveTurn>): Record<string, LiveTurn> {
  const now = Date.now();
  let changed = false;
  const next: Record<string, LiveTurn> = {};
  for (const [k, v] of Object.entries(prev)) {
    if (now - v.startedAt > LIVE_TURN_MAX_AGE_MS) {
      changed = true;
      continue;
    }
    next[k] = v;
  }
  return changed ? next : prev;
}
